import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TransactionHistory = ({ transactions }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('all');

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.companyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || transaction.type === filterType;
    
    let matchesDate = true;
    if (dateRange !== 'all') {
      const transactionDate = new Date(transaction.date);
      const now = new Date();
      const daysAgo = parseInt(dateRange);
      const cutoffDate = new Date(now.setDate(now.getDate() - daysAgo));
      matchesDate = transactionDate >= cutoffDate;
    }
    
    return matchesSearch && matchesType && matchesDate;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'buy': return 'Plus';
      case 'sell': return 'Minus';
      case 'dividend': return 'DollarSign';
      default: return 'ArrowUpDown';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'buy': return 'text-primary';
      case 'sell': return 'text-destructive';
      case 'dividend': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="card-elevated">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Transaction History</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
            <span className="ml-2">{isExpanded ? 'Collapse' : 'Expand'}</span>
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-4 animate-slide-in">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              type="search"
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Types</option>
              <option value="buy">Buy Orders</option>
              <option value="sell">Sell Orders</option>
              <option value="dividend">Dividends</option>
            </select>
            
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Time</option>
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 3 Months</option>
              <option value="365">Last Year</option>
            </select>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Symbol</th>
                  <th>Company</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="font-mono text-sm">{formatDate(transaction.date)}</td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={getTransactionIcon(transaction.type)} 
                          size={16} 
                          className={getTransactionColor(transaction.type)}
                        />
                        <span className={`capitalize ${getTransactionColor(transaction.type)}`}>
                          {transaction.type}
                        </span>
                      </div>
                    </td>
                    <td className="font-medium">{transaction.symbol}</td>
                    <td className="text-muted-foreground">{transaction.companyName}</td>
                    <td className="font-mono">{transaction.quantity.toLocaleString()}</td>
                                    <td className="font-mono">₹{transaction.price.toFixed(2)}</td>
                <td className="font-mono">₹{transaction.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {filteredTransactions.map((transaction) => (
              <div key={transaction.id} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={getTransactionIcon(transaction.type)} 
                      size={16} 
                      className={getTransactionColor(transaction.type)}
                    />
                    <span className={`capitalize font-medium ${getTransactionColor(transaction.type)}`}>
                      {transaction.type}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground font-mono">
                    {formatDate(transaction.date)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium text-foreground">{transaction.symbol}</p>
                    <p className="text-sm text-muted-foreground">{transaction.companyName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-foreground">
                      ₹{transaction.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-sm text-muted-foreground font-mono">
                      {transaction.quantity.toLocaleString()} @ ₹{transaction.price.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredTransactions.length === 0 && (
            <div className="text-center py-8">
              <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No transactions found matching your criteria.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;